/* eslint-disable no-unused-vars */

/* eslint-disable react/prop-types */

/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import LibSelect, { components } from 'react-select';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Icon from 'components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledGroup = styled(
  forwardRef(({ labeled, bottomSpace, ...props }, ref) => <Form.Group ref={ref} {...props} />)
)`
  position: relative;
  width: 100%;
  padding-top: ${({ labeled }) => (labeled ? spaces.smallX : 0)}px;

  ${({ bottomSpace }) =>
    bottomSpace &&
    css`
      margin-bottom: ${typeof bottomSpace === 'string' ? bottomSpace : `${bottomSpace}px`};
    `}
`;

const StyledSelectContainer = styled(
  forwardRef(({ textColor, placeholderColor, borderColor, backgroundColor, small, invalid, open, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  padding-top: ${spaces.smallX - 2}px;
  padding-bottom: ${spaces.smallX - 2}px;
  padding-left: ${spaces.regular}px;
  padding-right: ${spaces.regular}px;

  border: 2px solid ${hexToRGBA(colors.gray.secondary, 0.16)};
  border-radius: 12px;
  background-color: ${colors.white};

  ${({ small }) =>
    small &&
    css`
      padding-top: 0;
      padding-bottom: 0;
      padding-left: ${spaces.smallX}px;
      padding-right: ${spaces.smallX}px;
      border-radius: 8px;
    `}

  ${({ backgroundColor }) =>
    backgroundColor &&
    css`
      background-color: ${backgroundColor};
    `}

  ${({ borderColor }) =>
    borderColor &&
    css`
      border-color: ${borderColor};
    `}

  .base-select-container {
    .base-select__control {
      min-height: unset;
      border: none;
      background: none;
      cursor: pointer;

      .base-select__indicator-separator {
        display: none;
      }

      .base-select__indicator {
        &.base-select__dropdown-indicator {
          pointer-events: none;
        }
      }

      .base-select__indicator {
        padding: 0;
      }

      .base-select__value-container {
        padding: 0;

        .base-select__input-container {
          height: 24px;

          padding: 0;
          margin: 0;

          font-family: 'Inter';
          font-size: ${fontSizes.small}px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          color: ${colors.gray.primary};

          ${({ textColor }) =>
            textColor &&
            css`
              color: ${textColor};
              caret-color: ${textColor};
            `}

          input {
            height: 24px;
          }
        }

        .base-select__single-value {
          margin: 0;
          font-family: 'Inter';
          font-size: ${fontSizes.small}px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          color: ${colors.gray.primary};

          ${({ textColor }) =>
            textColor &&
            css`
              color: ${textColor};
              caret-color: ${textColor};
            `}
        }

        .base-select__placeholder {
          margin: 0;
          font-family: 'Inter';
          font-size: ${fontSizes.small}px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          color: ${colors.gray.secondary};

          ${({ placeholderColor }) =>
            placeholderColor &&
            css`
              color: ${placeholderColor};
            `}
        }
      }

      &.base-select__control--is-focused {
        box-shadow: none;
        border-color: ${colors.gray.secondary};
      }
    }

    .base-select__menu {
      min-width: 200px;
      right: 0;
      z-index: 2;

      .base-select__menu-list {
        .base-select__option {
          font-family: 'Inter';
          font-size: ${fontSizes.small}px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          color: ${colors.gray.primary};

          cursor: pointer;

          &.base-select__option--is-focused {
            background-color: ${hexToRGBA(colors.gray.secondary, 0.16)};
          }

          &.base-select__option--is-selected {
            background-color: ${colors.gray.secondary};
            color: ${colors.white};
          }
        }
      }
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${colors.white};
      opacity: 0.4;
    `}

  ${({ valid }) =>
    valid &&
    css`
      padding-right: ${spaces.smallX - 2}px;
    `}

  ${({ invalid }) =>
    invalid &&
    css`
      padding-right: ${spaces.smallX - 2}px;
    `}

  ${({ open }) =>
    open &&
    css`
      border-color: ${colors.gray.secondary};
    `}

  ${({ invalid, open }) =>
    invalid &&
    !open &&
    css`
      border-color: ${colors.red.primary};
    `}
`;

const StyledLabel = styled(forwardRef(({ invalid, textColor, ...props }, ref) => <div ref={ref} {...props} />))`
  position: absolute;
  top: 0;
  left: ${spaces.small}px;
  padding-left: ${spaces.smallX}px;
  padding-right: ${spaces.smallX}px;
  margin: 0;
  background-color: ${colors.white};

  font-family: 'Inter';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  color: ${colors.gray.secondary};

  z-index: 1;

  ${({ textColor }) =>
    textColor &&
    css`
      color: ${textColor};

      span {
        color: ${textColor};
      }
    `}

  ${({ invalid }) =>
    invalid &&
    css`
      color: ${colors.red.primary};
    `}
`;

const StyledIconContainer = styled(forwardRef(({ desktop, mobile, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`
  padding-left: ${spaces.smallest}px;

  font-family: 'Inter';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  &.processing-feedback {
    color: ${colors.gray.secondary};

    ${({ color }) =>
      color &&
      css`
        color: ${color};
      `};
  }

  &.valid-feedback {
    color: ${colors.green.primary};
  }

  &.invalid-feedback {
    color: ${colors.red.primary};
  }
`;

const Select = ({
  name,
  label,
  placeholder,
  bottomSpace,
  textColor,
  placeholderColor,
  borderColor,
  backgroundColor,
  small,
  clearable,
  searchable,
  disabled,
  required,
  valid,
  invalid,
  feedback,
  options = [],
  value,
  onChange,
  onBlur,
  ...restProps
}) => {
  const [open, setOpen] = useState(false);
  const [innerOptions, setInnerOptions] = useState([]);

  const currentValue = useMemo(
    () => innerOptions?.find((option) => option?.value === value) || null,
    [value, innerOptions]
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    onBlur && onBlur({ target: { name } });
  }, [name, onBlur]);

  const handleChange = useCallback(
    (option) => {
      onChange && onChange({ target: { name, value: option?.value || '' } });
    },
    [onChange, name]
  );

  useEffect(() => {
    setInnerOptions(
      options.map(({ value, title }) => {
        return { value, label: title };
      })
    );
  }, [options]);

  useEffect(() => {
    if (value && !currentValue && innerOptions.length) {
      onChange && onChange({ target: { name, value: null } });
    }
  }, [value, currentValue, name, innerOptions, onChange]);

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        {valid && (
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.tickSquare} color={colors.green.primary} />
          </StyledIconContainer>
        )}
        {invalid && (
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.closeSquare} color={colors.red.primary} />
          </StyledIconContainer>
        )}
        {!valid && !invalid && open && (
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.chevronUp} color={colors.gray.secondary} />
          </StyledIconContainer>
        )}
        {!valid && !invalid && !open && (
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.chevronDown} color={colors.gray.secondary} />
          </StyledIconContainer>
        )}
      </components.DropdownIndicator>
    );
  };

  return (
    <StyledGroup bottomSpace={bottomSpace} labeled={Boolean(label)} className={invalid ? 'custom-control-invalid' : ''}>
      {label && (
        <StyledLabel invalid={invalid} textColor={textColor}>
          {label}
          {label && required && <span>&nbsp;*</span>}
        </StyledLabel>
      )}
      <StyledSelectContainer
        textColor={textColor}
        placeholderColor={placeholderColor}
        borderColor={borderColor}
        backgroundColor={backgroundColor}
        small={small}
        disabled={disabled}
        valid={valid}
        invalid={invalid}
        open={open}
      >
        <LibSelect
          className="base-select-container"
          classNamePrefix="base-select"
          components={{ DropdownIndicator }}
          isClearable={clearable}
          isDisabled={disabled}
          isSearchable={searchable}
          options={innerOptions}
          placeholder={placeholder}
          value={currentValue}
          onChange={handleChange}
          onMenuOpen={handleOpen}
          onMenuClose={handleClose}
        />
      </StyledSelectContainer>
      <Form.Control readOnly hidden name={name} isValid={valid} isInvalid={invalid} {...restProps} />
      {!valid && !invalid && feedback && <StyledFeedback type="processing">{feedback}</StyledFeedback>}
      {feedback && <StyledFeedback type="valid">{feedback}</StyledFeedback>}
      {feedback && <StyledFeedback type="invalid">{feedback}</StyledFeedback>}
    </StyledGroup>
  );
};

Select.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  bottomSpace: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textColor: PropTypes.string,
  placeholderColor: PropTypes.string,
  borderColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  small: PropTypes.bool,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  required: PropTypes.bool,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

export default Select;
